import React from 'react';
import PropTypes from 'prop-types';
import ErrorMessage from './ErrorMessage';
import { InputNumber } from 'antd';
import styled from 'styled-components';
import { normalizePortalText } from 'utils/baochauTheme';
import useFormControl from './useFormControl';
import './style.scss';

const InputNumberStyled = styled(InputNumber)`
  width: 100%;

  &&.bw_form_number_control.ant-input-number,
  &&.bw_form_number_control.ant-input-number-group-wrapper {
    min-height: var(--bw-control-height);
    padding: 0 !important;
    border: 1px solid var(--bw-control-border) !important;
    border-radius: var(--bw-control-radius) !important;
    background: #fff !important;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
    overflow: hidden;
  }

  &&.bw_form_number_control.ant-input-number-group-wrapper .ant-input-number-wrapper {
    display: flex;
    width: 100%;
    min-height: calc(var(--bw-control-height) - 2px);
    align-items: stretch;
  }

  &&.bw_form_number_control.ant-input-number-group-wrapper .ant-input-number {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    min-height: calc(var(--bw-control-height) - 2px);
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  body
    .bw_contain
    .bw_main
    .bw_frm_box
    &&.bw_form_number_control.ant-input-number-group-wrapper
    .ant-input-number.ant-input-number,
  body
    .bw_contain
    .bw_main
    .bw_frm_box
    &&.bw_form_number_control.ant-input-number-group-wrapper
    .ant-input-number.ant-input-number:hover,
  body
    .bw_contain
    .bw_main
    .bw_frm_box
    &&.bw_form_number_control.ant-input-number-group-wrapper
    .ant-input-number.ant-input-number-focused {
    width: auto !important;
    min-width: 0 !important;
    min-height: calc(var(--bw-control-height) - 2px) !important;
    height: auto !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .ant-input-number-input-wrap {
    display: flex;
    align-items: center;
    height: calc(var(--bw-control-height) - 2px);
  }

  .ant-input-number-input {
    height: calc(var(--bw-control-height) - 2px);
    padding: 0 var(--bw-control-padding-x) !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  .ant-input-number-group-addon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    min-width: 56px;
    padding: ${(props) => (props.paddingnone ? '0 !important' : '0 14px !important')};
    border: 0 !important;
    border-left: 1px solid #e4e7ec !important;
    border-radius: 0 !important;
    background: #f8fafc !important;
    color: #344054;
    font-weight: 600;
    white-space: nowrap;
  }

  .ant-input-number-input::placeholder {
    color: #98a2b3;
  }
  .ant-input-number-input {
    color: #101828 !important;
    font-size: 14px;
  }

  &&.is-invalid.ant-input-number,
  &&.is-invalid.ant-input-number-group-wrapper {
    border-color: #fda29b !important;
    box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.1) !important;
  }

  &&.bw_form_number_control.ant-input-number:not(.ant-input-number-disabled):not(.is-invalid):hover,
  &&.bw_form_number_control.ant-input-number.ant-input-number-focused:not(.is-invalid),
  &&.bw_form_number_control.ant-input-number-group-wrapper:not(.ant-input-number-group-wrapper-disabled):not(
      .is-invalid
    ):hover,
  &&.bw_form_number_control.ant-input-number-group-wrapper:not(.is-invalid):focus-within {
    border-color: var(--bw-control-focus) !important;
    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.1) !important;
  }

  &&.bw_form_number_control.ant-input-number-disabled,
  &&.bw_form_number_control.ant-input-number-group-wrapper:has(.ant-input-number-disabled) {
    border-color: #e4e7ec !important;
    background: var(--bw-control-disabled-bg) !important;
    box-shadow: none !important;
  }

  &&.bw_form_number_control.ant-input-number-group-wrapper:has(.ant-input-number-disabled)
    .ant-input-number-group-addon {
    background: var(--bw-control-disabled-bg) !important;
    color: #667085;
  }
`;

const FormNumber = ({
  field,
  validation,
  bordered = false,
  value = null,
  disabled,
  readOnly,
  id,
  className,
  style,
  'aria-describedby': ariaDescribedBy,
  ...props
}) => {
  const { describedBy, error, errorId, fieldValue, hasError, inputId, setFieldValue } = useFormControl({
    field,
    validation,
    id,
    ariaDescribedBy,
  });

  return (
    <React.Fragment>
      <InputNumberStyled
        style={{
          width: '100%',
          ...style,
        }}
        id={inputId}
        className={['bw_form_number_control', className, hasError ? 'is-invalid' : '', readOnly ? 'is-readonly' : '']
          .filter(Boolean)
          .join(' ')}
        disabled={disabled || readOnly}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy}
        value={value !== null ? value : fieldValue}
        placeholder={normalizePortalText('0')}
        onChange={setFieldValue}
        controls={false}
        {...props}
      />
      {error && <ErrorMessage id={errorId} message={error?.message} />}
    </React.Fragment>
  );
};

FormNumber.propTypes = {
  bordered: PropTypes.bool,
  field: PropTypes.string,
  validation: PropTypes.object,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default FormNumber;
